"use client";

import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@components/ui/button.tsx";
import useDebounce from "@/hooks/useDebounce";
import { fetchListDestination } from "@/services/event.service";
import { DestinationItem } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { ChangeEvent } from "react";
import { CircularProgress } from "@mui/material";

interface IProps {
  value: string;
  onChange: (destination: DestinationItem | null) => void;
  name?: string;
  error?: string;
  isChooseAll?: boolean;
}

export default function SelectLocationCreateEvent({
  value,
  onChange,
  name,
  isChooseAll,
}: IProps) {
  const [textSearch, setTextSearch] = React.useState<string>("");
  const query = useDebounce(textSearch, 300);
  const [isFirstSearch, setFirstSearch] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState(false);

  const { data: dataSelected, isLoading: isLoadingDataSelected } = useQuery({
    queryKey: ["select_location_create_event_selected"],
    queryFn: () => fetchListDestination(name || ""),
    enabled: !!name && !query,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["select_location_create_event", query],
    queryFn: () => fetchListDestination(query),
    enabled: open,
  });

  const dataSelectList = React.useMemo(() => {
    if (data) {
      if (!query) {
        return [
          ...(dataSelected?.destinationDTOList || []),
          ...(data?.destinationDTOList || []),
        ];
      } else {
        return data?.destinationDTOList;
      }
    } else {
      return dataSelected?.destinationDTOList;
    }
    // else return dataSelected?.destinationDTOList;
  }, [data, dataSelected?.destinationDTOList, query]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setTextSearch(event.target.value);
    isFirstSearch && setFirstSearch(false);
  };

  const handleSelectLocation = (currentValue: string) => {
    const destinationSelect = dataSelectList?.find(
      (destination) => destination.destinationId === currentValue,
    );

    if (destinationSelect) {
      onChange(destinationSelect);
    } else {
      isChooseAll && onChange(null);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="line-clamp-1 flex h-12 w-full justify-start gap-4 rounded-lg border-[#E2E8F0] px-3 text-left font-normal text-neutral-950 shadow-none hover:bg-white"
        >
          {(isLoading || isLoadingDataSelected) && !name ? (
            <div className="flex items-center justify-center gap-2 py-2 text-[#94A3B8]">
              <CircularProgress size="12px" color="inherit" />
              Loading...
            </div>
          ) : name ? (
            <span className="line-clamp-1 text-left">{name}</span>
          ) : (
            <span className="w-full !text-left text-[#94A3B8]">
              Enter location of event
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full"
        style={{
          width: "var(--radix-popover-trigger-width)",
        }}
      >
        <div className="flex flex-col gap-2">
          <input
            tabIndex={-1}
            type="text"
            placeholder="Search Location..."
            value={textSearch}
            onChange={handleSearch}
            className={cn(
              "placeholder:text-[#94A3B8], h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal",
            )}
          />

          <div className="flex max-h-[270px] flex-col gap-3 overflow-y-auto">
            {dataSelectList?.map((destination) => (
              <div
                key={destination.destinationId}
                className="flex cursor-pointer items-center gap-1"
                onClick={() => {
                  handleSelectLocation(destination.destinationId);
                }}
              >
                <div className="w-4">
                  <Check
                    className={cn(
                      "h-4 w-4",
                      value === destination.destinationId
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </div>
                <span className="line-clamp-1 text-sm text-[#475569]">
                  {destination?.destinationName[0].name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
